<script setup lang="ts">
import { type PropType } from 'vue'
import { useVModels } from '@vueuse/core'
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Sheet,
	SheetContent,
	SheetClose,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import CommunityFilterContent from '@/components/CommunityFilterContent.vue'
import { useBreakpoints } from '@/lib/utils'
import type {
	ISorting,
	IWarehouseList,
	ICategory,
} from '@/legacy/components/ProductSharingPostList.vue'

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	sortMode: {
		type: String,
		default: '',
	},
	filterWarehouses: {
		type: Array as PropType<string[]>,
		default: () => [],
	},
	filterCategories: {
		type: Array as PropType<string[]>,
		default: () => [],
	},
	sortOptions: {
		type: Array as PropType<ISorting[]>,
		default: () => [],
	},
	warehouseOptions: {
		type: Array as PropType<IWarehouseList[]>,
		default: () => [],
	},
	categoryOptions: {
		type: Array as PropType<ICategory[]>,
		default: () => [],
	},
	filterSortTitles: {
		type: Object,
		default: () => {},
	},
	filterSortButtons: {
		type: Object,
		default: () => {},
	},
	isResetDisabled: {
		type: Boolean,
		required: true,
	},
})

const emit = defineEmits([
	'update:isOpen',
	'update:sortMode',
	'update:filterWarehouses',
	'update:filterCategories',
	'clearAll',
	'apply',
])
const { isOpen, sortMode, filterWarehouses, filterCategories } = useVModels(
	props,
	emit,
)

const { isTabletPortrait } = useBreakpoints()
</script>

<template>
	<Dialog v-if="isTabletPortrait" v-model:open="isOpen">
		<DialogContent
			class="shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10) max-h-[564px] max-w-[min(calc(100dvw-64px),796px)] grid-rows-[auto_minmax(0,1fr)_auto] rounded-lg border-none bg-white text-n-950 mdp:max-h-[492px] lg:max-h-[520px]"
		>
			<DialogHeader class="border-b border-b-n-100 py-16px pl-24px pr-20px">
				<DialogTitle
					class="typography-h5-str flex items-center justify-between text-left"
				>
					<div>{{ filterSortTitles.drawer }}</div>
					<DialogClose>
						<BaseIcon name="close_v1" class="h-24px w-24px" />
					</DialogClose>
				</DialogTitle>
			</DialogHeader>

			<CommunityFilterContent
				v-model:sortMode="sortMode"
				v-model:filterWarehouses="filterWarehouses"
				v-model:filterCategories="filterCategories"
				:sort-options="sortOptions"
				:warehouse-options="warehouseOptions"
				:category-options="categoryOptions"
				:filterSortTitles="filterSortTitles"
				:filterSortButtons="filterSortButtons"
				:isResetDisabled="isResetDisabled"
				@clearAll="emit('clearAll')"
				@apply="emit('apply')"
			/>
		</DialogContent>
	</Dialog>

	<Sheet v-else v-model:open="isOpen">
		<SheetContent
			side="bottom"
			class="shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10) rounded-t-lg border-none bg-white text-n-950"
		>
			<div class="flex h-[calc(100dvh-48px)] flex-col">
				<SheetHeader class="border-b border-b-n-100 py-12px pl-20px pr-16px">
					<SheetTitle
						class="typography-h5-str flex items-center justify-between gap-16px pl-[44px] text-center"
					>
						<div class="flex-grow">{{ filterSortTitles.drawer }}</div>
						<SheetClose>
							<BaseIcon name="close_v1" class="h-24px w-24px" />
						</SheetClose>
					</SheetTitle>
				</SheetHeader>

				<CommunityFilterContent
					v-model:sortMode="sortMode"
					v-model:filterWarehouses="filterWarehouses"
					v-model:filterCategories="filterCategories"
					:sort-options="sortOptions"
					:warehouse-options="warehouseOptions"
					:category-options="categoryOptions"
					:filterSortTitles="filterSortTitles"
					:filterSortButtons="filterSortButtons"
					:isResetDisabled="isResetDisabled"
					@clearAll="emit('clearAll')"
					@apply="emit('apply')"
				/>
			</div>
		</SheetContent>
	</Sheet>
</template>
